import { useQuery } from '@apollo/client';

import SkillsForRole from 'bundles/search-common/queries/SkillsForRole.graphql';
import type {
  SkillsForRoleQuery,
  SkillsForRoleQueryVariables,
} from 'bundles/search-common/queries/__generated__/SkillsForRole';

export type Skill = {
  id: string;
  skillName: string;
};

export const useSkillsForRoleQuery = (query: string) => {
  const matchedQueryID = query
    .trim()
    .toLowerCase()
    .replace(/[^a-z\s]/g, '') // Remove all non-alphabetic characters except spaces
    .split(' ')
    .join('-');

  const { data, loading, error } = useQuery<SkillsForRoleQuery, SkillsForRoleQueryVariables>(SkillsForRole, {
    variables: { jobName: 'browse_relevant_skills_by_role', query: matchedQueryID },
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    skip: !query,
  });

  // Transform the raw data into properly formatted skills
  const skills: Skill[] =
    data?.NaptimeQueries?.ExternallyAccessibleNostosV1Resource?.getAllProperties?.elements
      ?.map((element) => {
        if (!element?.content || typeof element.content !== 'object') {
          return null;
        }

        const content = element.content as { skillName?: string[] };
        if (!content.skillName || !Array.isArray(content.skillName)) {
          return null;
        }

        return content.skillName.map((skillName) => ({
          id: skillName.replace(/\s+/g, '-').toLowerCase(),
          skillName,
        }));
      })
      .filter((skill): skill is NonNullable<typeof skill> => skill !== null)
      .flat() || [];

  return { skills, loading, error };
};
